<template>
  <Modal
    size="lg"
    @close="$emit('close')"
  >
    <template #header>
      {{ projectGroup?.id ? 'Edit Project Group' : 'Create Project Group' }}
    </template>

    <template #body>
      <form
        class="space-y-8"
        @submit.prevent="save"
      >
        <section class="space-y-4">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Group Details
          </h3>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>

            <AppInput
              v-model="projectGroupForm.form.name"
              name="name"
              class="form-input w-full"
              type="text"
            />
          </div>
        </section>

        <section class="space-y-4 border-t border-slate-200 pt-6">
          <div class="flex items-center justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Shared Funds
              </h3>

              <p class="mt-1 text-xs text-slate-500">
                Budget pools shared across projects in this group.
              </p>
            </div>

            <button
              v-if="!projectGroup?.id"
              type="button"
              class="btn btn--secondary-blue text-sm shrink-0"
              @click="addSharedFund"
            >
              Add Fund
            </button>
          </div>

          <div v-if="!projectGroup?.id">
            <div
              v-for="(fund, index) in projectGroupForm.form.shared_funds"
              :key="index"
              class="grid grid-cols-3 gap-3 mb-3 p-4 border border-gray-200 rounded-lg bg-slate-50"
            >
              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">Name</label>

                <AppInput
                  v-model="fund.name"
                  :name="`shared_fund_name_${index}`"
                  class="form-input w-full"
                  type="text"
                />
              </div>

              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">Reference</label>

                <AppInput
                  v-model="fund.code"
                  :name="`shared_fund_code_${index}`"
                  class="form-input w-full"
                  type="text"
                  placeholder="Auto-generated"
                />
              </div>

              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">Budget</label>

                <AppCurrencyInput
                  v-model="fund.budget"
                  :name="`shared_fund_budget_${index}`"
                  class="form-input w-full"
                />
              </div>

              <div class="col-span-1 flex items-end">
                <button
                  v-if="projectGroupForm.form.shared_funds.length > 1"
                  type="button"
                  class="btn btn--secondary-alert text-sm w-full"
                  @click="removeSharedFund(index)"
                >
                  Remove
                </button>
              </div>
            </div>

            <p
              v-if="!projectGroupForm.form.shared_funds.length"
              class="text-sm text-slate-500 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-5 text-center"
            >
              No shared funds yet. Add one to set up a shared budget pool.
            </p>
          </div>

          <div
            v-else
            class="space-y-3"
          >
            <div
              v-for="(fund, index) in projectGroupForm.form.shared_funds"
              :key="index"
              class="grid grid-cols-3 gap-3 p-4 border border-gray-200 rounded-lg bg-slate-50"
            >
              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">Name</label>

                <AppInput
                  v-model="fund.name"
                  name="shared_fund_name[]"
                  class="form-input w-full"
                  type="text"
                />
              </div>

              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">Reference</label>

                <AppInput
                  v-model="fund.code"
                  name="shared_fund_code[]"
                  class="form-input w-full"
                  type="text"
                />
              </div>

              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">Budget</label>

                <AppCurrencyInput
                  v-model="fund.budget"
                  name="shared_fund_budget[]"
                  class="form-input w-full"
                />
              </div>

              <div class="flex items-end gap-2 col-span-2">
                <button
                  type="button"
                  class="btn btn--secondary-blue text-sm flex-1"
                  @click="saveSharedFund(fund)"
                >
                  Save
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-alert text-sm flex-1"
                  @click="deleteSharedFund(fund)"
                >
                  Delete
                </button>
              </div>
            </div>

            <div class="grid grid-cols-4 gap-3 p-4 border border-dashed border-gray-300 rounded-lg">
              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">New Fund Name</label>

                <AppInput
                  v-model="newSharedFund.name"
                  name="new_shared_fund_name"
                  class="form-input w-full"
                  type="text"
                />
              </div>

              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">Reference</label>

                <AppInput
                  v-model="newSharedFund.code"
                  name="new_shared_fund_code"
                  class="form-input w-full"
                  type="text"
                  placeholder="Auto-generated"
                />
              </div>

              <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-600 mb-1">Budget</label>

                <AppCurrencyInput
                  v-model="newSharedFund.budget"
                  name="new_shared_fund_budget"
                  class="form-input w-full"
                />
              </div>

              <div class="col-span-1 flex items-end">
                <button
                  type="button"
                  class="btn btn--secondary-blue text-sm w-full"
                  :disabled="!newSharedFund.name"
                  @click="createSharedFund"
                >
                  Add Fund
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="space-y-4 border-t border-slate-200 pt-6">
          <div class="flex items-center justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Projects
              </h3>

              <p class="mt-1 text-xs text-slate-500">
                Select which company projects belong to this group.
              </p>
            </div>

            <span class="text-xs text-slate-500 shrink-0">
              {{ selectedProjectCount }} selected
            </span>
          </div>

          <AppInputIcon
            v-model="projectSearchFilter"
            type="search"
            name="project_search_filter"
            class="form-input pl-10"
            placeholder="Search projects..."
          >
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-slate-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>
            </template>
          </AppInputIcon>

          <div
            v-if="filteredAvailableProjects.length === 0"
            class="text-sm text-slate-500 rounded-lg border border-slate-200 bg-white px-4 py-5 text-center"
          >
            {{ projectSearchFilter ? 'No projects match your search' : 'No projects available' }}
          </div>

          <div
            v-else
            class="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100"
          >
            <label
              v-for="companyProject in filteredAvailableProjects"
              :key="companyProject.id"
              class="flex cursor-pointer items-start gap-3 px-4 py-3 text-sm transition-colors"
              :class="projectRowClass(companyProject.id)"
            >
              <input
                v-model="projectGroupForm.form.project_ids"
                type="checkbox"
                class="form-checkbox mt-0.5 shrink-0"
                :value="companyProject.id"
              >

              <span class="flex-1 min-w-0">
                <span class="font-semibold text-slate-900 truncate block">
                  {{ companyProject.name }}
                </span>

                <span
                  v-if="companyProject.reference"
                  class="text-xs text-slate-500 truncate block"
                >
                  {{ companyProject.reference }}
                </span>
              </span>
            </label>
          </div>

          <div
            v-if="selectedProjects.length > 0"
            class="space-y-2"
          >
            <h4 class="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Selected Projects
            </h4>

            <ul class="rounded-lg border border-slate-200 divide-y divide-slate-100">
              <li
                v-for="selectedProject in selectedProjects"
                :key="selectedProject.id"
                class="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
              >
                <span class="font-medium text-slate-900 truncate">
                  {{ selectedProject.name }}
                </span>

                <button
                  type="button"
                  class="btn-icon btn-icon-blue shrink-0"
                  title="Remove project"
                  @click="removeProject(selectedProject.id)"
                >
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
                </button>
              </li>
            </ul>
          </div>
        </section>
      </form>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <button
          type="button"
          class="btn btn--ghost-default"
          @click="$emit('close')"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="!projectGroupForm.form.name"
          @click="save"
        >
          Save
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { useProjectGroupsStore } from '@/store/projectGroups';
import { useProjectsStore } from '@/store/projects';
import { useSharedFundsStore } from '@/store/sharedFunds';
import Swal from 'sweetalert2';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

type SharedFundForm = {
  id?: number;
  name: string;
  code: string;
  budget: number;
};

const props = defineProps<{
  companyId: number;
  projectGroupId: Id;
}>();

const emit = defineEmits<{
  close: [];
  saved: [];
}>();

const projectGroupsStore = useProjectGroupsStore();
const sharedFundsStore = useSharedFundsStore();
const projectsStore = useProjectsStore();
const createProjectGroupAction = useStoreApiAction(projectGroupsStore.create);
const updateProjectGroupAction = useStoreApiAction(projectGroupsStore.update);
const createSharedFundAction = useStoreApiAction(sharedFundsStore.create);
const updateSharedFundAction = useStoreApiAction(sharedFundsStore.update);
const fetchProjectsAction = useStoreApiAction(projectsStore.fetchProjects);

const newSharedFund = reactive({
  name: '',
  code: '',
  budget: 0,
});

const projectGroupForm = useFormData({
  name: '',
  shared_funds: [] as { name: string; code: string; budget: number }[],
  project_ids: [],
});

const projectGroup = computed(() => {
  return projectGroupsStore.models.with('shared_funds').with('projects').find(props.projectGroupId);
});

watch(projectGroup, (newValue) => {
  if(newValue) {
    projectGroupForm.form.name = newValue.name;

    projectGroupForm.form.shared_funds = newValue.shared_funds?.map((fund) => ({
      id: fund.id,
      name: fund.name,
      code: fund.code ?? '',
      budget: fund.budget,
    })) ?? [];

    projectGroupForm.form.project_ids = newValue.projects?.map((project) => project.id) ?? [];
  }
}, { immediate: true });

const projectSearchFilter = ref('');

const availableProjects = computed(() => {
  return projectsStore.models
    .where('company_id', props.companyId)
    .get()
    .filter((project) => !project.project_group_id);
});

const filteredAvailableProjects = computed(() => {
  if(!projectSearchFilter.value) {
    return availableProjects.value;
  }

  const filter = projectSearchFilter.value.toLowerCase();

  return availableProjects.value.filter((project) =>
    project.name?.toLowerCase().includes(filter) ||
    project.reference?.toLowerCase().includes(filter)
  );
});

const selectedProjects = computed(() => {
  return projectGroupForm.form.project_ids
    .map((id) => projectsStore.models.find(id))
    .filter((project) => !!project);
});

const selectedProjectCount = computed(() => projectGroupForm.form.project_ids.length);

const projectRowClass = (projectId: number) => {
  return projectGroupForm.form.project_ids.includes(projectId) ?
    'bg-blue-50' :
    'bg-white hover:bg-slate-50';
};

const removeProject = (projectId: number) => {
  projectGroupForm.form.project_ids = projectGroupForm.form.project_ids.filter((id) => id !== projectId);
};

onMounted(() => {
  fetchProjectsAction.request();
});

const addSharedFund = () => {
  projectGroupForm.form.shared_funds.push({ name: '', code: '', budget: 0 });
};

const removeSharedFund = (index: number) => {
  projectGroupForm.form.shared_funds.splice(index, 1);
};

const saveSharedFund = (fund: SharedFundForm) => {
  if(!fund.id) {
    return;
  }

  updateSharedFundAction.request(fund.id, {
    name: fund.name,
    code: fund.code,
    budget: fund.budget,
  }).then(() => {
    Swal.fire({ icon: 'success', title: 'Shared fund updated', timer: 1500, showConfirmButton: false });
  }).catch((error) => {
    console.error(error);
  });
};

const createSharedFund = () => {
  if(!props.projectGroupId || !newSharedFund.name) {
    return;
  }

  createSharedFundAction.request(props.projectGroupId, {
    name: newSharedFund.name,
    code: newSharedFund.code,
    budget: newSharedFund.budget,
  }).then((data) => {
    newSharedFund.name = '';
    newSharedFund.code = '';
    newSharedFund.budget = 0;
  }).catch((error) => {
    Swal.fire({ icon: 'error', title: 'Failed to create shared fund', text: error.data?.message });
  });
};

const deleteSharedFund = (fund: SharedFundForm) => {
  if(!fund.id) {
    return;
  }

  Swal.fire({
    icon: 'warning',
    title: 'Delete shared fund?',
    showCancelButton: true,
  }).then((result) => {
    if(!result.isConfirmed) {
      return;
    }

    sharedFundsStore.delete(fund.id);
  });
};

const save = () => {
  if(props.projectGroupId) {
    updateProjectGroupAction.request(props.projectGroupId, {
      name: projectGroupForm.form.name,
      project_ids: Array.from(projectGroupForm.form.project_ids),
    }).then(() => {
      emit('saved');
      emit('close');
      toast.success('Project group updated');
    }).catch((error) => {
      console.error(error);
    });

    return;
  }

  createProjectGroupAction.request(props.companyId, projectGroupForm.form).then(() => {
    emit('saved');
    emit('close');
    toast.success('Project group created');
  }).catch((error) => {
    console.error(error);
  });
};
</script>
